import clsx from 'clsx';
import { MouseEvent, ReactNode, useCallback, useEffect, useRef } from 'react';

export interface ModalFrameProps {
  backdrop?: boolean;
  children: ReactNode;
  color?: keyof typeof ModalFrameColor;
  classes?: string;
  closeOnClickOutside?: boolean;
  closeOnEsc?: boolean;
  open?: boolean;
  onClose?: () => void;
  size?: keyof typeof ModalFrameSize;
  variant?: keyof typeof ModalFrameVariant;
}

const ModalFrameColor = {
  default: 'bg-white text-black',
  dark: 'bg-cinder text-white'
};

const ModalFrameSize = {
  sm: 'px-0',
  md: 'px-14'
};

const ModalFrameVariant = {
  default: '',
  rounded: 'rounded-semi-md'
};

export const ModalFrame = ({
  backdrop = true,
  children,
  color = 'default',
  classes,
  closeOnClickOutside,
  closeOnEsc,
  open,
  onClose,
  size = 'sm',
  variant = 'default'
}: ModalFrameProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    onClose?.();

    document.body.style.overflow = 'unset';
  }, [onClose]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        handleClose();
      }
    },
    [handleClose, open]
  );

  useEffect(() => {
    if (!closeOnEsc) return;

    document.addEventListener('keydown', onKeyDown, false);

    return () => {
      document.removeEventListener('keydown', onKeyDown, false);
    };
  }, [closeOnEsc, onKeyDown]);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (!closeOnClickOutside) return;

      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [closeOnClickOutside, handleClose]
  );

  const hasCustomPositioning = classes?.includes('fixed') || classes?.includes('absolute');

  if (hasCustomPositioning) {
    return (
      <>
        {backdrop && <div className="fixed inset-0 bg-black/80 z-backdrop" />}

        <div
          ref={ref}
          className={clsx(
            'z-10',
            ModalFrameSize[size],
            ModalFrameColor[color],
            ModalFrameVariant[variant],
            classes
          )}
        >
          {children}
        </div>
      </>
    );
  }

  return (
    <div
      className={clsx('fixed inset-0 flex items-center justify-center z-top', {
        'bg-black/80': backdrop
      })}
      onClick={handleClickOutside}
    >
      <div
        ref={ref}
        className={clsx(
          'relative',
          ModalFrameSize[size],
          ModalFrameColor[color],
          ModalFrameVariant[variant],
          classes
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};
